import { create } from "zustand";
import { persist } from "zustand/middleware";
import vendorSeedData, { SEED_VERSION } from "../data/vendorSeedData";
import {
  REVIEW_STATES,
  RISK_LEVELS,
  VENDOR_SOURCES,
  SECURITY_REVIEW_STATUS,
  RECOMMENDATIONS,
  DECISIONS,
} from "../utils/constants";
import { calculateInherentRisk, calculateResidualRisk } from "../utils/riskScoring";
import { resolveSoc2Files } from "../utils/knownVendorReports";

const STORAGE_KEY = "regodit-vendor-store";

const clone = (value) =>
  typeof structuredClone === "function"
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

const seededState = () => ({
  vendors: clone(vendorSeedData.vendors),
  discovered: clone(vendorSeedData.discovered),
  monitoring: clone(vendorSeedData.monitoring),
});

const nowIso = () => new Date().toISOString();

const genId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const withActivity = (vendor, action, actor) => ({
  ...vendor,
  activity: [
    {
      id: genId("a"),
      at: nowIso(),
      actor: actor || vendor.owner || "Vendor owner",
      action,
    },
    ...(vendor.activity || []),
  ],
});

const mapReview = (reviews, reviewId, updater) =>
  (reviews || []).map((r) => (r.id === reviewId ? updater(r) : r));

const recommendationToDecision = (recommendation) => {
  if (recommendation === RECOMMENDATIONS.APPROVED) return DECISIONS.APPROVE;
  if (recommendation === RECOMMENDATIONS.CONDITIONALLY_APPROVED) {
    return DECISIONS.APPROVE_WITH_EXCEPTIONS;
  }
  if (recommendation === RECOMMENDATIONS.NOT_APPROVED) return DECISIONS.REJECT;
  return null;
};

const residualForRecommendation = (inherentRisk, recommendation) => {
  if (recommendation === RECOMMENDATIONS.APPROVED) {
    return calculateResidualRisk(inherentRisk, 0.9);
  }
  if (recommendation === RECOMMENDATIONS.CONDITIONALLY_APPROVED) {
    return calculateResidualRisk(inherentRisk, 0.55);
  }
  if (recommendation === RECOMMENDATIONS.NOT_APPROVED) {
    return calculateResidualRisk(inherentRisk, 0.2);
  }
  return RISK_LEVELS.UNSCORED;
};

const buildDraftReview = ({ title, owner, files = [] }) => ({
  id: genId("r"),
  title,
  status: SECURITY_REVIEW_STATUS.DRAFT,
  createdAt: nowIso(),
  completedAt: null,
  discardedAt: null,
  owner: owner || "",
  recommendation: null,
  summary: "",
  files,
});

const useVendorStore = create(
  persist(
    (set, get) => ({
      ...seededState(),

      resetVendorData: () => set(seededState()),

      getVendorById: (id) => get().vendors.find((v) => v.id === id),

      updateVendorOwner: (id, ownerEmail) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            return withActivity(
              {
                ...v,
                owner: ownerEmail || "",
                reviews: (v.reviews || []).map((r) =>
                  r.status === SECURITY_REVIEW_STATUS.DRAFT
                    ? { ...r, owner: ownerEmail || "" }
                    : r
                ),
              },
              ownerEmail ? "Updated vendor owner" : "Cleared vendor owner",
              ownerEmail || ""
            );
          }),
        })),

      assignOwnersFromStaff: (people) => {
        const emails = (people || []).map((p) => p.email).filter(Boolean);
        if (!emails.length) return;
        const valid = new Set(emails);
        set((state) => ({
          vendors: state.vendors.map((v, i) => {
            const nextOwner = valid.has(v.owner) ? v.owner : emails[i % emails.length];
            const reviews = (v.reviews || []).map((r) => ({
              ...r,
              owner: valid.has(r.owner) ? r.owner : nextOwner,
            }));
            const findings = (v.findings || []).map((f) => ({
              ...f,
              owner: valid.has(f.owner) ? f.owner : nextOwner,
            }));
            if (nextOwner === v.owner) {
              return { ...v, reviews, findings };
            }
            return { ...v, owner: nextOwner, reviews, findings };
          }),
        }));
      },

      addVendor: (vendor) =>
        set((state) => {
          const inputs = vendor.riskInputs || {};
          const { level } = calculateInherentRisk(inputs);
          const newVendor = {
            id: genId("v"),
            logoColor: "#031B89",
            status: "Active",
            source: VENDOR_SOURCES.MANUAL,
            reviewState: REVIEW_STATES.UPCOMING,
            lastReviewedAt: null,
            nextReviewDate: null,
            reviewCycleDays: 365,
            riskInputs: inputs,
            inherentRisk: level,
            residualRisk: RISK_LEVELS.UNSCORED,
            decision: null,
            decisionNotes: "",
            reviews: [],
            evidence: [],
            findings: [],
            activity: [
              { id: genId("a"), at: nowIso(), actor: vendor.owner || "You", action: "Added vendor" },
            ],
            ...vendor,
          };
          return { vendors: [newVendor, ...state.vendors] };
        }),

      archiveVendor: (id) =>
        set((state) => ({
          vendors: state.vendors.map((v) =>
            v.id === id
              ? withActivity(
                  { ...v, status: v.status === "Archived" ? "Active" : "Archived" },
                  v.status === "Archived" ? "Restored vendor" : "Archived vendor"
                )
              : v
          ),
        })),

      addDiscoveredVendor: (discoveredId) =>
        set((state) => {
          const item = state.discovered.find((d) => d.id === discoveredId);
          if (!item) return {};
          const newVendor = {
            id: genId("v"),
            name: item.name,
            logoColor: item.logoColor,
            website: item.website,
            category: item.category,
            owner: "",
            source: VENDOR_SOURCES.DISCOVERED,
            discoveredFrom: item.discoveredFrom,
            services: `Detected via ${item.discoveredFrom} (${item.users} users)`,
            contactName: "",
            contactEmail: "",
            trustCenterUrl: item.trustCenterUrl || "",
            authType: "SSO / SAML",
            contractStart: "",
            contractEnd: "",
            status: "Active",
            reviewState: REVIEW_STATES.UPCOMING,
            lastReviewedAt: null,
            nextReviewDate: null,
            reviewCycleDays: 365,
            riskInputs: {},
            inherentRisk: item.suggestedRisk || RISK_LEVELS.UNSCORED,
            residualRisk: RISK_LEVELS.UNSCORED,
            decision: null,
            decisionNotes: "",
            reviews: [],
            evidence: [],
            findings: [],
            activity: [
              {
                id: genId("a"),
                at: nowIso(),
                actor: "You",
                action: `Added to inventory from ${item.discoveredFrom}`,
              },
            ],
          };
          return {
            vendors: [newVendor, ...state.vendors],
            discovered: state.discovered.filter((d) => d.id !== discoveredId),
          };
        }),

      ignoreDiscoveredVendor: (discoveredId) =>
        set((state) => ({
          discovered: state.discovered.filter((d) => d.id !== discoveredId),
        })),

      updateRiskInputs: (id, riskInputs) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const { level } = calculateInherentRisk(riskInputs);
            return withActivity(
              { ...v, riskInputs, inherentRisk: level },
              `Updated inherent risk to ${level}`
            );
          }),
        })),

      startReview: (id) => {
        let createdId = null;
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const existingDraft = (v.reviews || []).find(
              (r) => r.status === SECURITY_REVIEW_STATUS.DRAFT
            );
            if (existingDraft) {
              createdId = existingDraft.id;
              return v;
            }
            const nextNumber = (v.reviews || []).length + 1;
            const knownFiles = resolveSoc2Files(v).map((file) => ({
              ...file,
              id: file.id || genId("e"),
              uploadedAt: file.uploadedAt || nowIso(),
              source: file.source || "Trust Center",
            }));
            const draft = buildDraftReview({
              title: `Security Review - ${nextNumber}`,
              owner: v.owner || "",
              files: knownFiles,
            });
            createdId = draft.id;
            return withActivity(
              {
                ...v,
                reviewState: REVIEW_STATES.DUE,
                reviews: [...(v.reviews || []), draft],
              },
              "Started security review"
            );
          }),
        }));
        return createdId;
      },

      hydrateReviewFiles: (id, reviewId) =>
        set((state) => {
          const vendor = state.vendors.find((v) => v.id === id);
          const review = vendor?.reviews?.find((r) => r.id === reviewId);
          if (!vendor || !review || (review.files || []).length) return {};
          const knownFiles = resolveSoc2Files(vendor).map((file) => ({
            ...file,
            id: file.id || genId("e"),
            uploadedAt: file.uploadedAt || nowIso(),
            source: file.source || "Trust Center",
          }));
          if (!knownFiles.length) return {};
          return {
            vendors: state.vendors.map((v) =>
              v.id === id
                ? {
                    ...v,
                    reviews: mapReview(v.reviews, reviewId, (r) => ({ ...r, files: knownFiles })),
                  }
                : v
            ),
          };
        }),

      updateReview: (id, reviewId, patch) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            return {
              ...v,
              reviews: mapReview(v.reviews, reviewId, (r) => ({ ...r, ...patch })),
            };
          }),
        })),

      addReviewFile: (id, reviewId, file) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const newFile = { id: file.id || genId("e"), uploadedAt: nowIso(), ...file };
            return withActivity(
              {
                ...v,
                evidence: [newFile, ...(v.evidence || [])],
                reviews: mapReview(v.reviews, reviewId, (r) => ({
                  ...r,
                  files: [newFile, ...(r.files || [])],
                })),
              },
              `Uploaded file: ${file.name}`
            );
          }),
        })),

      removeReviewFile: (id, reviewId, fileId) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const removed = (v.reviews || [])
              .find((r) => r.id === reviewId)
              ?.files?.find((f) => f.id === fileId);
            return withActivity(
              {
                ...v,
                reviews: mapReview(v.reviews, reviewId, (r) => ({
                  ...r,
                  files: (r.files || []).filter((f) => f.id !== fileId),
                })),
              },
              `Removed file: ${removed?.name || "document"}`
            );
          }),
        })),

      completeReview: (id, reviewId) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const review = (v.reviews || []).find((r) => r.id === reviewId);
            if (!review?.recommendation) return v;
            const decision = recommendationToDecision(review.recommendation);
            const residualRisk = residualForRecommendation(v.inherentRisk, review.recommendation);
            const next = new Date();
            next.setDate(next.getDate() + (v.reviewCycleDays || 365));
            return withActivity(
              {
                ...v,
                decision,
                residualRisk,
                decisionNotes: review.summary || "",
                reviewState: REVIEW_STATES.COMPLETED,
                lastReviewedAt: nowIso(),
                nextReviewDate: next.toISOString(),
                reviews: mapReview(v.reviews, reviewId, (r) => ({
                  ...r,
                  status: SECURITY_REVIEW_STATUS.COMPLETED,
                  completedAt: nowIso(),
                  discardedAt: null,
                })),
              },
              `Recorded decision: ${review.recommendation}`
            );
          }),
        })),

      discardReview: (id, reviewId) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            return withActivity(
              {
                ...v,
                reviews: mapReview(v.reviews, reviewId, (r) => ({
                  ...r,
                  status: SECURITY_REVIEW_STATUS.DISCARDED,
                  discardedAt: nowIso(),
                  completedAt: null,
                })),
              },
              "Discarded security review"
            );
          }),
        })),

      addFinding: (id, finding) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const newFinding = {
              id: genId("f"),
              createdAt: nowIso(),
              ...finding,
            };
            return withActivity(
              { ...v, findings: [newFinding, ...(v.findings || [])] },
              `Logged finding: ${finding.title}`
            );
          }),
        })),

      updateFinding: (id, findingId, patch) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            return {
              ...v,
              findings: (v.findings || []).map((f) =>
                f.id === findingId ? { ...f, ...patch } : f
              ),
            };
          }),
        })),

      addEvidence: (id, evidence) =>
        set((state) => ({
          vendors: state.vendors.map((v) => {
            if (v.id !== id) return v;
            const newEvidence = { id: genId("e"), uploadedAt: nowIso(), ...evidence };
            return withActivity(
              { ...v, evidence: [newEvidence, ...(v.evidence || [])] },
              `Added evidence: ${evidence.name}`
            );
          }),
        })),

      acknowledgeAlert: (alertId) =>
        set((state) => ({
          monitoring: state.monitoring.map((m) =>
            m.id === alertId ? { ...m, acknowledged: true } : m
          ),
        })),
    }),
    {
      name: STORAGE_KEY,
      version: SEED_VERSION,
      migrate: () => seededState(),
    }
  )
);

if (typeof window !== "undefined") {
  window.__regoResetVendors = () => {
    useVendorStore.getState().resetVendorData();
    // eslint-disable-next-line no-console
    console.info("[Regodit] Vendor data re-seeded.");
  };
}

export default useVendorStore;
